import type { PdpEcommerceBlockProductDetailsProps } from '@design-system/components/Pdp/EcommerceBlock/Product/PdpEcommerceBlockProductDetails.props'
import type { ReserveShop } from '@design-system/components/Pdp/EcommerceBlock/ReserveInStore/PdpEcommerceBlockReserveInStoreShopAddress.props'
import type { ReserveFormPersonalInfo } from '@design-system/components/Pdp/EcommerceBlock/ReserveInStore/PdpEcommerceBlockReserveInStoreStepTwo.props'
import type { BookAndReserveRequest } from '../schemas/bookAndReserveSchema'
import type { BookAnAppointmentForm } from './useBookAnAppointment'
import type { ShopObject } from '../schemas/bookAnAppointmentShopsSchema'

/**
 * Composable for managing book and reserve functionality.
 * Provides methods for reserving products in-store and booking appointments.
 *
 * @returns {Promise<Object>} An object containing methods for fetching reservations and appointments
 *
 * @example
 * const { fetchReserve, fetchBookAppontment } = await useBookAndReserve();
 */
export const useBookAndReserve = async () => {
  const { country, language } = useRouteHelper()
  const { getCurrencySymbolFromCountryCode, currentCurrency } = useCurrency()
  const { formatPriceByLocale } = useFormatPrice()
  const { customer } = await useCustomer()

  /**
   * Parses the Salesforce customer ID.
   *
   * @returns {string} Parsed Salesforce ID or empty string
   * @private
   */
  const parsedSalesforceId = () => {
    if (customer && customer.value.id) return customer.value.id.split('|')[1]
    return ''
  }

  /**
   * Determines the customer's language.
   *
   * @returns {string} Customer language or default language
   * @private
   */
  const parsedCustomerLanguage = () => {
    if (customer && customer.value.language && customer.value.language !== '')
      return customer.value.language
    return language
  }

  /**
   * Determines the customer's country.
   *
   * @returns {string} Customer country or default country
   * @private
   */
  const parsedCustomerCountry = () => {
    if (customer && customer.value.country && customer.value.country !== '')
      return customer.value.country
    return country
  }

  /**
   * Retrieves the product currency symbol.
   *
   * @returns {string} Currency symbol or empty string
   * @private
   */
  const parsedProductCurrency = () => {
    const currencyFromCountry = getCurrencySymbolFromCountryCode(country)
    if (currencyFromCountry) return currencyFromCountry.symbol as string
    return ''
  }

  /**
   * Generates a fully qualified product image URL.
   *
   * @param {string} productImage - Relative product image path
   * @returns {string} Fully qualified image URL
   * @private
   */
  const parsedProductImage = (productImage: string) => {
    return `https://assets.armani.com/image/upload/${productImage}`
  }

  /**
   * Fetches a product reservation in a specific store.
   *
   * @param {ReserveShop} shop - Selected store details
   * @param {ReserveFormPersonalInfo} formPersonalInfo - Customer personal information
   * @param {PdpEcommerceBlockProductDetailsProps} product - Product details
   * @param {string} productImage - Product image path
   * @param {string} idProduct - Product identifier
   * @param {string} selectedSize - Selected product size
   * @param {string} email - Store contact email
   *
   * @returns {Promise<any>} Reservation request response
   *
   * @throws {Error} If reservation request fails
   *
   * @example
   * await fetchReserve(
   *   storeDetails,
   *   personalInfo,
   *   productDetails,
   *   'product-image.jpg',
   *   'PROD123',
   *   'M',
   *   'store@example.com'
   * );
   */
  const fetchReserve = async (
    shop: ReserveShop,
    formPersonalInfo: ReserveFormPersonalInfo,
    product: PdpEcommerceBlockProductDetailsProps,
    productImage: string,
    idProduct: string,
    selectedSize: string,
    email: string
  ) => {
    const body: BookAndReserveRequest = {
      emailType: 'reserve_in_store',
      storeName: shop.name,
      storeEmail: email,
      storeId: shop.code,
      productCode: idProduct,
      productName: product.title,
      productPrice: formatPriceByLocale(currentCurrency.value, product.price),
      productCurrency: parsedProductCurrency(),
      productImage: parsedProductImage(productImage),
      size: selectedSize,
      salesforceId: parsedSalesforceId(),
      customerCountry: parsedCustomerCountry(),
      customerTitle: formPersonalInfo.gender,
      customerName: formPersonalInfo.firstName,
      customerLastname: formPersonalInfo.lastName,
      customerEmail: formPersonalInfo.email,
      customerPhonePrefix: formPersonalInfo.phonePrefix,
      customerPhone: formPersonalInfo.phoneNumber,
      customerMessage: formPersonalInfo.message,
      customerLanguage: parsedCustomerLanguage(),
    }
    return $fetch('/api/bookAndReserve/', {
      method: 'POST',
      body,
    })
  }

  /**
   * Fetches an appointment booking for a specific store.
   *
   * @param {ShopObject} shop - Selected store details
   * @param {BookAnAppointmentForm} bookAppointmentForm - Appointment booking form details
   *
   * @returns {Promise<any>} Appointment booking request response
   *
   * @throws {Error} If appointment booking request fails
   *
   * @example
   * await fetchBookAppontment(
   *   storeDetails,
   *   appointmentForm
   * );
   */
  const fetchBookAppontment = async (
    shop: ShopObject,
    bookAppointmentForm: BookAnAppointmentForm
  ) => {
    const body: BookAndReserveRequest = {
      emailType: 'book_an_appointment',
      storeName: shop.name,
      storeEmail: shop.emails?.[0] || '',
      storeId: shop.c_siteID,
      // storeNumber: shop.number,
      productCode: '',
      productName: '',
      productPrice: '',
      productCurrency: parsedProductCurrency(),
      productImage: '',
      size: '',
      salesforceId: parsedSalesforceId(),
      customerCountry: parsedCustomerCountry(),
      customerTitle: '',
      customerName: bookAppointmentForm.firstName,
      customerLastname: bookAppointmentForm.lastName,
      customerEmail: bookAppointmentForm.email,
      customerPhonePrefix: bookAppointmentForm.phonePrefix,
      customerPhone: bookAppointmentForm.phoneNumber,
      customerMessage: bookAppointmentForm.message,
      customerLanguage: parsedCustomerLanguage(),
    }
    return $fetch('/api/bookAndReserve/', {
      method: 'POST',
      body,
    })
  }

  return {
    fetchReserve,
    fetchBookAppontment,
  }
}
